// Growth Junction first-party attribution bootstrap.
//
// This is injected as the FIRST script in <head> (see src/routes/__root.tsx),
// i.e. before any CMP, before Google Consent Mode defaults, before GTM and
// before every analytics / advertising tag.
//
// IMPORTANT — CONSENT NOTE:
// This module is INTENTIONALLY independent of Google Consent Mode v2. It never
// waits for ad_storage / analytics_storage / ad_user_data / ad_personalization,
// never calls gtag, never pushes to the dataLayer and never issues a request to
// Google, Meta, Microsoft or any other third party. It only reads the landing
// page URL and the browser-provided referrer, keeps first-party attribution
// state in first-party cookies / localStorage and populates this website's own
// forms. No names, e-mail addresses, phone numbers or other lead PII are ever
// written to attribution storage.
// The legal / CMP basis for this first-party operational lead attribution must
// be reviewed separately for every market in which this template is deployed.

export const ATTRIBUTION_CONFIG = {
  version: '1.0.0',
  cookiePrefix: 'gj_attr_',
  cookieDomain: '',
  retentionDays: 180,
  sessionMinutes: 30,
  persistBeforeConsent: true,
}

export const ATTRIBUTION_BOOTSTRAP = `(function(){
try{
// Guard against tag managers re-submitting a fetch-based form natively.
// Runs before gtm.js loads, so any reference GTM keeps points at this wrapper.
try {
  var __protoSubmit = HTMLFormElement.prototype.submit;
  HTMLFormElement.prototype.submit = function(){
    if (this && this.getAttribute && this.getAttribute('data-gj-no-native-submit') === '1') return;
    return __protoSubmit.apply(this, arguments);
  };
} catch(e){}
var CFG = ${JSON.stringify(ATTRIBUTION_CONFIG)};
var W = window, D = document;
if (W.gjAttribution) return;

var CLICK_IDS = ['gclid','gbraid','wbraid','dclid','msclkid','fbclid','ttclid','li_fat_id','twclid','sccid','epik','rdt_cid','gad_source','gad_campaignid','gclsrc'];
var PAID_KEYS = ['campaign_name','campaign_id','adgroup_name','adgroup_id','ad_id','creative','keyword','matchtype','network','device','device_model','placement','placement_category','adposition','target_id','asset_group_id','ad_type','loc_physical_ms','loc_interest_ms','extension_id','feed_item_id','video_source_id'];
var PAID_ALIASES = {campaignid:'campaign_id', adgroupid:'adgroup_id', adid:'ad_id', targetid:'target_id', assetgroupid:'asset_group_id', extensionid:'extension_id', feeditemid:'feed_item_id', videosourceid:'video_source_id', locphysical:'loc_physical_ms', locinterest:'loc_interest_ms', devicemodel:'device_model'};
var TOUCH_KEYS = ['channel','source','medium','campaign','campaign_id','content','term','source_platform','landing_page','referrer','timestamp','has_utm'];

function now(){ return Date.now(); }
function iso(t){ try { return new Date(t).toISOString(); } catch(e){ return ''; } }
function uid(){
  try {
    if (W.crypto && W.crypto.randomUUID) return W.crypto.randomUUID();
    if (W.crypto && W.crypto.getRandomValues) {
      var a = new Uint8Array(16); W.crypto.getRandomValues(a);
      var s=''; for (var i=0;i<16;i++){ s += ('0'+a[i].toString(16)).slice(-2); }
      return s;
    }
  } catch(e){}
  return 'x'+now().toString(16)+Math.floor(Math.random()*1e9).toString(16);
}

function writeCookie(name, value){
  try {
    var v = encodeURIComponent(value);
    var exp = new Date(now() + CFG.retentionDays*864e5).toUTCString();
    var str = CFG.cookiePrefix+name+'='+v+'; Expires='+exp+'; Path=/; SameSite=Lax';
    if (CFG.cookieDomain) str += '; Domain='+CFG.cookieDomain;
    if (location.protocol === 'https:') str += '; Secure';
    D.cookie = str;
  } catch(e){}
}
function readCookie(name){
  try {
    var key = CFG.cookiePrefix+name+'=';
    var parts = D.cookie ? D.cookie.split('; ') : [];
    for (var i=0;i<parts.length;i++){
      if (parts[i].indexOf(key) === 0) return decodeURIComponent(parts[i].slice(key.length));
    }
  } catch(e){}
  return '';
}
// localStorage is a supplementary first-party store (cookie size safety net).
function save(name, obj){
  var json = '';
  try { json = JSON.stringify(obj); } catch(e){ return; }
  writeCookie(name, json);
  try { W.localStorage.setItem(CFG.cookiePrefix+name, json); } catch(e){}
}
function load(name){
  var raw = readCookie(name);
  if (!raw) { try { raw = W.localStorage.getItem(CFG.cookiePrefix+name) || ''; } catch(e){} }
  if (!raw) return null;
  try { return JSON.parse(raw); } catch(e){ return null; }
}

function params(){
  var out = {};
  try {
    var q = location.search || '';
    if (q.charAt(0) === '?') q = q.slice(1);
    if (!q) return out;
    var pairs = q.split('&');
    for (var i=0;i<pairs.length;i++){
      if (!pairs[i]) continue;
      var idx = pairs[i].indexOf('=');
      var k = idx < 0 ? pairs[i] : pairs[i].slice(0, idx);
      var v = idx < 0 ? '' : pairs[i].slice(idx+1);
      try { k = decodeURIComponent(k.replace(/\\+/g,' ')); } catch(e){}
      // Click IDs and ValueTrack values are stored verbatim: only a single
      // percent-decoding pass, no trimming, no case changes.
      try { v = decodeURIComponent(v.replace(/\\+/g,' ')); } catch(e){}
      if (v !== '') out[k] = v;
    }
  } catch(e){}
  return out;
}

function host(url){
  try { return new URL(url).hostname.replace(/^www\\./,'').toLowerCase(); } catch(e){ return ''; }
}
var SEARCH_HOSTS = /(^|\\.)(google\\.[a-z.]+|bing\\.com|duckduckgo\\.com|yahoo\\.[a-z.]+|ecosia\\.org|search\\.brave\\.com|baidu\\.com|yandex\\.[a-z.]+|startpage\\.com|qwant\\.com)$/;
var SOCIAL_HOSTS = /(^|\\.)(facebook\\.com|instagram\\.com|linkedin\\.com|lnkd\\.in|t\\.co|twitter\\.com|x\\.com|tiktok\\.com|pinterest\\.[a-z.]+|reddit\\.com|snapchat\\.com|xing\\.com|youtube\\.com)$/;

function classifyMedium(m){
  m = (m||'').toLowerCase();
  if (!m) return '';
  if (/^(cpc|ppc|paidsearch|paid_search|sea|adwords|google_ads)$/.test(m)) return 'paid_search';
  if (/(display|banner|cpm|gdn)/.test(m)) return 'display';
  if (/(paid.?social|social.?paid|paidsocial)/.test(m)) return 'paid_social';
  if (/(^social$|social)/.test(m)) return 'organic_social';
  if (/(^email$|newsletter|mail)/.test(m)) return 'email';
  if (/affiliate/.test(m)) return 'affiliate';
  if (/^organic$/.test(m)) return 'organic_search';
  if (/referr?al/.test(m)) return 'referral';
  return 'other';
}

function buildTouch(p, ref){
  var t = {};
  for (var i=0;i<TOUCH_KEYS.length;i++) t[TOUCH_KEYS[i]] = '';
  t.timestamp = iso(now());
  t.landing_page = location.href;
  t.referrer = ref || '';

  var hasUtm = !!(p.utm_source || p.utm_medium || p.utm_campaign);
  // Source priority: 1) explicit UTMs 2) advertising click IDs
  //                  3) external referrer 4) direct
  if (hasUtm) {
    t.has_utm = '1';
    t.source = p.utm_source || '';
    t.medium = p.utm_medium || '';
    t.campaign = p.utm_campaign || '';
    t.campaign_id = p.utm_id || p.campaign_id || '';
    t.content = p.utm_content || '';
    t.term = p.utm_term || '';
    t.source_platform = p.utm_source_platform || '';
    t.channel = classifyMedium(p.utm_medium) || 'other';
    if (!t.medium) t.channel = 'other';
    return t;
  }
  if (p.gclid || p.gbraid || p.wbraid) {
    t.source = 'google'; t.medium = 'cpc'; t.channel = 'paid_search';
    t.campaign_id = p.campaign_id || ''; t.term = p.keyword || '';
    t.source_platform = 'google_ads';
    return t;
  }
  if (p.dclid) { t.source='google'; t.medium='display'; t.channel='display'; return t; }
  if (p.msclkid) { t.source='bing'; t.medium='cpc'; t.channel='paid_search'; t.source_platform='microsoft_ads'; return t; }
  if (p.ttclid) { t.source='tiktok'; t.medium='paid_social'; t.channel='paid_social'; return t; }
  if (p.twclid) { t.source='twitter'; t.medium='paid_social'; t.channel='paid_social'; return t; }
  if (p.li_fat_id) { t.source='linkedin'; t.medium='paid_social'; t.channel='paid_social'; return t; }
  if (p.sccid) { t.source='snapchat'; t.medium='paid_social'; t.channel='paid_social'; return t; }
  if (p.epik) { t.source='pinterest'; t.medium='paid_social'; t.channel='paid_social'; return t; }
  if (p.rdt_cid) { t.source='reddit'; t.medium='paid_social'; t.channel='paid_social'; return t; }
  // NOTE: fbclid alone is NOT proof of paid traffic — organic Facebook links
  // carry it too, so it falls through to referrer / direct classification.

  var rh = host(ref);
  if (rh && rh !== host(location.href)) {
    t.source = rh;
    if (SEARCH_HOSTS.test(rh)) { t.medium='organic'; t.channel='organic_search'; }
    else if (SOCIAL_HOSTS.test(rh)) { t.medium='social'; t.channel='organic_social'; }
    else { t.medium='referral'; t.channel='referral'; }
    return t;
  }
  // Internal referrers never create a new acquisition source.
  t.source = '(direct)'; t.medium = '(none)'; t.channel = 'direct';
  return t;
}

function isPaidish(t){ return t && t.channel && t.channel !== 'direct'; }

// ---------------------------------------------------------------- state ----
var p = params();
var ref = '';
try { ref = D.referrer || ''; } catch(e){}

var visitor = load('visitor');
if (!visitor || !visitor.id) { visitor = { id: uid(), created: iso(now()) }; }
save('visitor', visitor);

var session = load('session');
var fresh = !session || !session.id || !session.last ||
  (now() - Date.parse(session.last)) > CFG.sessionMinutes*6e4;
if (fresh) session = { id: uid(), started: iso(now()), last: iso(now()) };
else session.last = iso(now());
save('session', session);

var touch = buildTouch(p, fresh ? ref : '');

// First touch is written once and never overwritten during retention.
var first = load('first_touch');
if (!first || !first.timestamp) { first = touch; save('first_touch', first); }

// Last touch = the acquisition session immediately before submission.
// A genuinely new direct session must stay (direct)/(none)/direct.
var last = load('last_touch');
if (fresh || !last) { last = touch; save('last_touch', last); }
else if (isPaidish(touch) && !isPaidish(last)) { last = touch; save('last_touch', last); }

// Click IDs live separately from first/last touch and survive later direct
// sessions. A newer identifier replaces the older one for the same platform.
var clicks = load('click_ids') || {};
var clickChanged = false;
for (var ci=0; ci<CLICK_IDS.length; ci++){
  var ck = CLICK_IDS[ci];
  if (p[ck]) { clicks[ck] = p[ck]; clicks[ck+'__ts'] = iso(now()); clickChanged = true; }
}
if (clickChanged || !load('click_ids')) save('click_ids', clicks);

// Paid-media detail values are also kept separately and are not erased by a
// later direct session.
var paid = load('paid_data') || {};
var paidChanged = false;
for (var pi=0; pi<PAID_KEYS.length; pi++){
  var pk = PAID_KEYS[pi];
  if (p[pk]) { paid[pk] = p[pk]; paidChanged = true; }
}
for (var alias in PAID_ALIASES){
  if (Object.prototype.hasOwnProperty.call(PAID_ALIASES, alias) && p[alias]) {
    paid[PAID_ALIASES[alias]] = p[alias]; paidChanged = true;
  }
}
if (paidChanged || !load('paid_data')) save('paid_data', paid);

function touchOut(t){
  var o = {};
  for (var i=0;i<TOUCH_KEYS.length;i++){
    if (TOUCH_KEYS[i] === 'has_utm') continue;
    o[TOUCH_KEYS[i]] = (t && t[TOUCH_KEYS[i]]) || '';
  }
  return o;
}
function cleanClicks(){
  var o = {};
  for (var i=0;i<CLICK_IDS.length;i++){ if (clicks[CLICK_IDS[i]]) o[CLICK_IDS[i]] = clicks[CLICK_IDS[i]]; }
  return o;
}

function snapshot(formEl){
  var formId = '', formName = '';
  if (formEl && formEl.getAttribute) {
    formId = formEl.getAttribute('data-form-id') || formEl.id || '';
    formName = formEl.getAttribute('data-form-name') || '';
  }
  return {
    attribution_version: CFG.version,
    visitor_id: visitor.id,
    session_id: session.id,
    form_id: formId,
    form_name: formName,
    form_page_url: location.href,
    form_page_title: D.title || '',
    form_submitted_at: iso(now()),
    first_touch: touchOut(first),
    last_touch: touchOut(last),
    click_ids: cleanClicks(),
    paid_data: paid
  };
}

function getFields(formEl){
  var s = snapshot(formEl);
  var f = {};
  f.attribution_version = s.attribution_version;
  f.visitor_id = s.visitor_id;
  f.session_id = s.session_id;
  f.form_id = s.form_id;
  f.form_name = s.form_name;
  f.form_page_url = s.form_page_url;
  f.form_page_title = s.form_page_title;
  f.form_submitted_at = s.form_submitted_at;
  var k;
  for (k in s.first_touch){ if (Object.prototype.hasOwnProperty.call(s.first_touch,k)) f['first_touch_'+k] = s.first_touch[k]; }
  for (k in s.last_touch){ if (Object.prototype.hasOwnProperty.call(s.last_touch,k)) f['last_touch_'+k] = s.last_touch[k]; }
  for (k in s.click_ids){ if (Object.prototype.hasOwnProperty.call(s.click_ids,k)) f[k] = s.click_ids[k]; }
  for (k in s.paid_data){ if (Object.prototype.hasOwnProperty.call(s.paid_data,k)) f[k] = s.paid_data[k]; }
  // Legacy flat fields kept for the existing dashboard columns.
  if (last && last.has_utm === '1') {
    f.utm_source = last.source; f.utm_medium = last.medium; f.utm_campaign = last.campaign;
    f.utm_id = last.campaign_id; f.utm_term = last.term; f.utm_content = last.content;
    f.utm_source_platform = last.source_platform;
  }
  f.landing_url = (first && first.landing_page) || location.href;
  f.referrer = (last && last.referrer) || (first && first.referrer) || '';
  f.attribution_json = JSON.stringify(s);
  return f;
}

function fillForm(formEl){
  if (!formEl || !formEl.querySelectorAll) return;
  var fields = getFields(formEl);
  var inputs = formEl.querySelectorAll('input[type="hidden"][data-gj-attr]');
  for (var i=0;i<inputs.length;i++){
    var n = inputs[i].getAttribute('name');
    if (!n) continue;
    var v = fields[n];
    inputs[i].value = (v === undefined || v === null) ? '' : String(v);
  }
}
function fillForms(){
  try {
    var forms = D.querySelectorAll('form');
    for (var i=0;i<forms.length;i++) fillForm(forms[i]);
  } catch(e){}
}

W.gjAttribution = {
  config: CFG,
  snapshot: snapshot,
  getFields: getFields,
  fillForm: fillForm,
  fillForms: fillForms
};

// Populate on init, on insertion (debounced MutationObserver), on focus and
// as a fallback on the native submit event.
function start(){
  fillForms();
  try {
    var t = null;
    var obs = new MutationObserver(function(){
      if (t) return;
      t = setTimeout(function(){ t = null; fillForms(); }, 200);
    });
    obs.observe(D.documentElement || D.body, { childList: true, subtree: true });
  } catch(e){}
  D.addEventListener('focusin', function(e){
    var el = e.target;
    while (el && el.tagName !== 'FORM') el = el.parentElement;
    if (el) fillForm(el);
  }, true);
  D.addEventListener('submit', function(e){ fillForm(e.target); }, true);
}
if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', start);
else start();
}catch(err){ try { console.warn('gj attribution bootstrap failed', err); } catch(e){} }
})();`
